import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { visuallyHidden } from '@mui/utils';
import useReport from './useReport';
import { MenuItem, Select, FormControl, InputLabel, TableSortLabel } from '@mui/material';

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: 'contact',
    numeric: false,
    disablePadding: true,
    label: 'Contact',
  },
  {
    id: 'orderCount',
    numeric: true,
    disablePadding: false,
    label: 'Order Amount',
  },
  {
    id: 'amount_500',
    numeric: true,
    disablePadding: false,
    label: '< 500',
  },
  {
    id: 'amount_500_2000',
    numeric: true,
    disablePadding: false,
    label: '500 - 2000',
  },
  {
    id: 'amount_2000_5000',
    numeric: true,
    disablePadding: false,
    label: '2000 - 5000',
  },
  {
    id: 'amount_5000_10000',
    numeric: true,
    disablePadding: false,
    label: '5000 - 10000',
  },
  {
    id: 'amount_10000_50000',
    numeric: true,
    disablePadding: false,
    label: '10000 - 50000',
  },
  {
    id: 'amount_50000_plus',
    numeric: true,
    disablePadding: false,
    label: '50000 +',
  },
  {
    id: 'total',
    numeric: true,
    disablePadding: false,
    label: 'Total Receipt',
  },
];

function EnhancedTableHead(props) {
  const {  order, orderBy, onRequestSort } =
    props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow className='bg-blue-300' key={Math.random()}>
        {headCells.map((headCell) => (
          <TableCell
            style={{ fontWeight : 'bold'}}
            key={headCell.id}
            align={'left'}
            padding={'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              style={{ fontWeight : 'bold'}}
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

export default function EnhancedTable() {
  const [order, setOrder] = React.useState('desc');
  const [orderBy, setOrderBy] = React.useState('total');
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [dense, setDense] = React.useState(false);
  const [rowsPerPage, setRowsPerPage] = React.useState(100);

  const {reportData, isLoading, getReceiptAmountReport } = useReport();
  const [period, setPeriod] = useState('7'); // Default to 7 days

  useEffect(() => {
    getReceiptAmountReport(period);
  }, [period]);

  const handlePeriodChange = (event) => {
    setPeriod(event.target.value);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 100));
    setPage(0);
  };

  
  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - reportData.length) : 0;

    const visibleRows = React.useMemo(() => {
      // Convert data to numeric values as needed
      const convertedData = reportData.map(row => ({
        ...row,
        orderCount: Number(row.orderCount),
        amount_500: Number(row.amount_500),
        amount_500_2000: Number(row.amount_500_2000),
        amount_2000_5000: Number(row.amount_2000_5000),
        amount_5000_10000: Number(row.amount_5000_10000),
        amount_10000_50000: Number(row.amount_10000_50000),
        amount_50000_plus: (row.amount_50000_plus),
        total: (row.amount_500 + row.amount_500_2000 + row.amount_2000_5000 +
                    row.amount_5000_10000 + row.amount_10000_50000 + row.amount_50000_plus),
      }));
    
      return stableSort(convertedData, getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [order, orderBy, page, rowsPerPage, reportData]);
    

  return (
    <Box sx={{ width: '100%' }}>
      <FormControl sx={{ margin: 2, minWidth: 120 }}>
        <InputLabel id="select-period-label">Period</InputLabel>
        <Select
          labelId="select-period-label"
          id="select-period"
          value={period}
          label="Period"
          onChange={handlePeriodChange}
        >
          <MenuItem value={'7'}>7 Days</MenuItem>
          <MenuItem value={'30'}>30 Days</MenuItem>
          <MenuItem value={'90'}>90 Days</MenuItem>
          <MenuItem value={'180'}>180 Days</MenuItem>
          <MenuItem value={'all'}>All Time</MenuItem>
        </Select>
      </FormControl>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size={'small'}
          >
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={reportData.length}
            />
            <TableBody>
              {visibleRows.map((row, index) => {
                return (
                  <TableRow>
                    <TableCell
                      component="th"
                      id={row.contact}
                      scope="row"
                      padding="normal"
                    >
                      {row.contact}
                    </TableCell>
                    <TableCell align="left">{`${row.orderCount}`}</TableCell>
                    <TableCell align="left">{`${row.amount_500} (${row.amount_500 && Math.round(row.sum_500/row.amount_500)})`}</TableCell>
                    <TableCell align="left">{`${row.amount_500_2000} (${row.amount_500_2000 && Math.round(row.sum_500_2000/row.amount_500_2000)})`}</TableCell>
                    <TableCell align="left">{`${row.amount_2000_5000} (${row.amount_2000_5000 && Math.round(row.sum_2000_5000/row.amount_2000_5000)})`}</TableCell>
                    <TableCell align="left">{`${row.amount_5000_10000} (${row.amount_5000_10000 && Math.round(row.sum_5000_10000/row.amount_5000_10000)})`}</TableCell>
                    <TableCell align="left">{`${row.amount_10000_50000} (${row.amount_10000_50000 && Math.round(row.sum_10000_50000/row.amount_10000_50000)})`}</TableCell>
                    <TableCell align="left">{`${row.amount_50000_plus} (${row.amount_50000_plus && Math.round(row.sum_50000_plus/row.amount_50000_plus)})`}</TableCell>
                    <TableCell align="left">{`${row.amount_500 + row.amount_500_2000 + row.amount_2000_5000 + row.amount_5000_10000 + row.amount_10000_50000 + row.amount_50000_plus}`}</TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: (dense ? 33 : 53) * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 100]}
          component="div"
          count={reportData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
}